import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Modal,
  notification,
  Input,
  Select,
  Dropdown,
  MenuProps,
  Pagination,
  Card
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  SearchOutlined,
  KeyOutlined,
  StopOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { UserProps, AuthTokenType } from '../utils/types';
import { getAuthToken } from '../utils/functions';
import { UsersUrl, ForgotPasswordUrl } from '../utils/network';
import axios from 'axios';
import dayjs from 'dayjs';

const { Option } = Select;

interface UserManagementProps {
  approvedUsers: UserProps[];
  onRefreshUsers: () => void;
  onEditUser: (user: UserProps) => void;
  loading: boolean;
  searchTerm?: string;
  selectedRole?: string;
}

const ROLE_CONFIG = {
  Admin: {
    label: "Admin",
    description: "Full system administration access",
    color: "#fee2e2",
    textColor: "#991b1b",
    dotColor: "#dc2626"
  },
  User: {
    label: "User",
    description: "Standard user access with basic operations",
    color: "#dbeafe",
    textColor: "#1e40af",
    dotColor: "#2563eb"
  }
};

export const UserManagement: React.FC<UserManagementProps> = ({
  approvedUsers,
  onRefreshUsers,
  onEditUser,
  loading,
  searchTerm = '',
  selectedRole = 'all'
}) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    visible: boolean;
    user: UserProps | null;
  }>({
    visible: false,
    user: null
  });
  const [resetLoading, setResetLoading] = useState(false);

  const [suspendModal, setSuspendModal] = useState<{
    visible: boolean;
    user: UserProps | null;
  }>({
    visible: false,
    user: null
  });
  const [suspendLoading, setSuspendLoading] = useState(false);

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    user: UserProps | null;
  }>({
    visible: false,
    user: null
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [editModal, setEditModal] = useState<{
    visible: boolean;
    user: UserProps | null;
    newRole: string;
    newStatus: boolean;
    newFullname: string;
    newEmail: string;
  }>({
    visible: false,
    user: null,
    newRole: "",
    newStatus: true,
    newFullname: "",
    newEmail: ""
  });
  const [editLoading, setEditLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });

  // Filter users based on search and filters
  const filteredUsers = approvedUsers.filter(user => {
    const matchesSearch = !searchTerm ||
      user.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = selectedRole === "all" || user.role?.toLowerCase() === selectedRole?.toLowerCase();
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && user.is_active) ||
      (statusFilter === "inactive" && !user.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Auto-adjust page if deleting a user or filtering makes the current page empty
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredUsers.length, currentPage, pageSize]);

  const pagedUsers = useMemo(() => filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ), [filteredUsers, currentPage, pageSize]);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);
    
    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [filteredUsers, loading, pagedUsers]); 

  const handleDeleteUser = (user: UserProps) => {
    setDeleteModal({
      visible: true,
      user: user
    });
  };

  const confirmDeleteUser = async () => {
    if (!deleteModal.user) return;

    setDeleteLoading(true);
    try {
      const headers = getAuthToken() as AuthTokenType;
      await axios.delete(`${UsersUrl}/${deleteModal.user.id}`, headers);

      notification.success({
        message: "User Deleted",
        description: `${deleteModal.user.fullname}'s account has been removed.`,
        placement: 'topRight'
      });

      setDeleteModal({ visible: false, user: null });
      onRefreshUsers();

    } catch (error: any) {
      notification.error({
        message: "Delete Error",
        description: error.response?.data?.error || "Failed to delete user account.",
        placement: 'topRight'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditUser = (user: UserProps) => {
    setEditModal({
      visible: true,
      user: user,
      newRole: user.role,
      newStatus: user.is_active,
      newFullname: user.fullname || "",
      newEmail: user.email || ""
    });
  };

  const confirmEditUser = async () => {
    if (!editModal.user) return;

    setEditLoading(true);
    try {
      const headers = getAuthToken() as AuthTokenType;
      const updateData = {
        ...editModal.user,
        role: editModal.newRole,
        is_active: editModal.newStatus,
        fullname: editModal.newFullname,
        email: editModal.newEmail
      };

      await axios.put(`${UsersUrl}/${editModal.user.id}`, updateData, headers);

      setEditModal({
        visible: false,
        user: null,
        newRole: "",
        newStatus: true,
        newFullname: "",
        newEmail: ""
      });

      notification.success({
        message: "User Updated",
        description: `Changes to ${editModal.newFullname} saved.`,
        placement: 'topRight'
      });

      onRefreshUsers();

    } catch (error: any) {
      notification.error({
        message: "Update Error",
        description: error.response?.data?.error || "Failed to update user account.",
        placement: 'topRight'
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleUserStatus = (user: UserProps) => {
    setSuspendModal({
      visible: true,
      user: user
    });
  };

  const confirmToggleStatus = async () => {
    if (!suspendModal.user) return;

    const action = suspendModal.user.is_active ? "suspend" : "activate";
    setSuspendLoading(true);
    try {
      const headers = getAuthToken() as AuthTokenType;
      await axios.put(`${UsersUrl}/${suspendModal.user.id}`, {
        ...suspendModal.user,
        is_active: !suspendModal.user.is_active
      }, headers);

      notification.success({
        message: `User ${action === 'suspend' ? 'Suspended' : 'Activated'}`,
        description: `Account for ${suspendModal.user.fullname} updated.`
      });

      setSuspendModal({ visible: false, user: null });
      onRefreshUsers();
    } catch (error: any) {
      notification.error({
        message: `Status Update Error`,
        description: error.response?.data?.error || `Failed to ${action} user account.`
      });
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleResetPassword = (user: UserProps) => {
    setResetPasswordModal({
      visible: true,
      user: user
    });
  };

  const confirmResetPassword = async () => {
    if (!resetPasswordModal.user) return;

    setResetLoading(true);
    try {
      await axios.post(ForgotPasswordUrl, {
        email: resetPasswordModal.user.email
      });

      notification.success({
        message: "Reset Email Sent",
        description: `Sent to ${resetPasswordModal.user.email}.`
      });

      setResetPasswordModal({ visible: false, user: null });
    } catch (error: any) {
      notification.error({
        message: "Reset Error",
        description: error.response?.data?.error || "Failed to send reset email."
      });
    } finally {
      setResetLoading(false);
    }
  };

  const getActionMenuItems = (user: UserProps): MenuProps['items'] => [
    {
      key: 'edit',
      label: <span className="font-['Lexend',sans-serif] text-[13px] font-medium">Edit Profile</span>,
      icon: <EditOutlined className="text-[#4682B4]" />,
      onClick: () => handleEditUser(user)
    },
    {
      key: 'reset-password',
      label: <span className="font-['Lexend',sans-serif] text-[13px] font-medium">Reset Password</span>,
      icon: <KeyOutlined className="text-[#4682B4]" />,
      onClick: () => handleResetPassword(user)
    },
    {
      key: 'toggle-status',
      label: <span className="font-['Lexend',sans-serif] text-[13px] font-medium">{user.is_active ? 'Suspend User' : 'Activate User'}</span>,
      icon: user.is_active ? <StopOutlined className="text-[#f59e0b]" /> : <PlayCircleOutlined className="text-[#10b981]" />,
      onClick: () => handleToggleUserStatus(user)
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: <span className="font-['Lexend',sans-serif] text-[13px] font-medium">Delete Account</span>,
      icon: <DeleteOutlined className="text-[#ef4444]" />,
      danger: true,
      onClick: () => handleDeleteUser(user)
    }
  ];

  const columns: ColumnsType<UserProps> = [
    {
      title: <span className="font-['Lexend',sans-serif] uppercase tracking-wider text-[11px] font-bold text-[#64748b]">User Details</span>,
      key: 'user',
      render: (_, record) => (
        <div className="flex items-center gap-4 py-1">
          <div className="w-10 h-10 rounded-full bg-[#4682B4]/10 flex items-center justify-center text-[#4682B4] font-bold font-['Lexend',sans-serif]">
            {record.fullname?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-['Lexend',sans-serif] font-semibold text-[14px] text-[#1e293b] leading-tight mb-1">
              {record.fullname}
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-[#64748b] font-medium">
              <MailOutlined className="text-[11px]" />
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <span className="font-['Lexend',sans-serif] uppercase tracking-wider text-[11px] font-bold text-[#64748b]">System Role</span>,
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
        return (
          <div
            className="inline-flex items-center px-2.5 py-1 rounded-full gap-2"
            style={{ backgroundColor: config?.color || '#f1f5f9', color: config?.textColor || '#64748b' }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config?.dotColor || '#94a3b8' }} />
            <span className="font-['Lexend',sans-serif] text-[12px] font-semibold">{config?.label || role}</span>
          </div>
        );
      },
    },
    {
      title: <span className="font-['Lexend',sans-serif] uppercase tracking-wider text-[11px] font-bold text-[#64748b]">Status</span>,
      key: 'status',
      render: (_, record) => (
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-['Lexend',sans-serif] text-[12px] font-bold ${record.is_active ? 'bg-[#10b981]/10 text-[#10b981]' : 'bg-[#ef4444]/10 text-[#ef4444]'
          }`}>
          <div className={`w-1.5 h-1.5 rounded-full ${record.is_active ? 'bg-[#10b981]' : 'bg-[#ef4444]'}`} />
          {record.is_active ? 'Active' : 'Inactive'}
        </div>
      ),
    },
    {
      title: <span className="font-['Lexend',sans-serif] uppercase tracking-wider text-[11px] font-bold text-[#64748b]">Last Activity</span>,
      dataIndex: 'last_login',
      key: 'last_login',
      render: (lastLogin: string) => (
        <div className="flex flex-col">
          <span className="font-['DM_Sans',sans-serif] font-semibold text-[13px] text-[#475569]">
            {lastLogin ? dayjs(lastLogin).format('MMM DD, YYYY') : 'Never Logged In'}
          </span>
          {lastLogin && (
            <span className="text-[11px] text-[#94a3b8] font-medium">
              at {dayjs(lastLogin).format('HH:mm A')}
            </span>
          )}
        </div>
      ),
    },
    {
      title: <span className="font-['Lexend',sans-serif] uppercase tracking-wider text-[11px] font-bold text-[#64748b]">Actions</span>,
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <div className='flex justify-center items-center w-full'>
          <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button className="flex items-center justify-center h-9 w-9 rounded-xl hover:bg-[#4682B4]/5 text-[#64748b] hover:text-[#4682B4] transition-all border-none bg-transparent cursor-pointer">
            <MoreOutlined className="text-[18px]" />
          </button>
        </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div className="p-1">
      <style dangerouslySetInnerHTML={{__html: `
        .modern-table .ant-table-thead > tr > th {
          background: #4682B417 !important;
          color: #1F2A37 !important;
          font-weight: 400 !important;
          font-family: 'Lexend', sans-serif !important;
          border-bottom: none !important;
          padding: 16px !important;
          font-size: 10px !important;
        }
        @media (min-width: 768px) {
          .modern-table .ant-table-thead > tr > th {
            font-size: 14px !important;
          }
        }
        .modern-table .ant-table {
          background: transparent !important;
        }
        .modern-table .ant-table-container {
          background: transparent !important;
        }
        .modern-table .ant-table-row {
          transition: all 0.2s;
          background: transparent !important;
        }
        .modern-table .ant-table-row:hover {
          background-color: #F8FAFC !important;
        }
        .modern-table .ant-table-cell {
          border-bottom: 1px solid #F1F5F9 !important;
          padding: 16px !important;
          font-family: 'Lexend', sans-serif !important;
          background: transparent !important;
          font-size: 14px !important;
        }
        .modern-table .ant-table-content::-webkit-scrollbar {
          display: none !important;
        }
        .modern-table .ant-table-content {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}} />
      <Card
        className=""
        bodyStyle={{ padding: '16px' }}
      >
        <div ref={tableContainerRef}>
          <Table
            columns={columns}
            dataSource={pagedUsers}
            rowKey="id"
            loading={loading}
            pagination={false}
            className="modern-table"
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <div className="py-12 flex flex-col items-center">
                  <SearchOutlined className="text-[40px] text-[#cbd5e1] mb-3" />
                  <div className="font-['Lexend',sans-serif] font-semibold text-[#64748b]">No users found</div>
                  <div className="text-[13px] text-[#94a3b8]">Try adjusting your search or filters</div>
                </div>
              )
            }}
          />
        </div>
      </Card>

      {/* Custom Synchronized Scrollbar Outside the card */}
      {scrollInfo.width > scrollInfo.client && (
        <div className="w-full flex justify-center mt-[19px]">
          <div 
            className="relative h-[4px] rounded-[3px] overflow-hidden" 
            style={{ 
              width: '85.90%', 
              backgroundColor: '#4682B430' 
            }}
          >
            <div 
              className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
              style={{
                width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Separate Pagination outside the card */}
      <div className="w-full flex justify-end mt-[22px] md:mt-[42px]">
        <style dangerouslySetInnerHTML={{__html: `
          .custom-pagination-container {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            width: 100% !important;
          }
          .custom-pagination-container .ant-pagination-total-text {
            order: 1 !important;
            color: #555D67 !important;
            font-family: 'Lexend', sans-serif !important;
            font-size: 10px !important;
            font-weight: 300 !important;
            margin-right: 8px !important;
          }
          @media (min-width: 768px) {
            .custom-pagination-container .ant-pagination-total-text {
              font-size: 14px !important;
              margin-right: 12px !important;
            }
          }
          /* Arrows Styling */
          .custom-pagination-container .ant-pagination-prev,
          .custom-pagination-container .ant-pagination-next {
            order: 2 !important;
            border: none !important;
            background: transparent !important;
            min-width: 20px !important;
            height: 32px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            color: #1F2A37 !important;
            font-size: 16px !important;
            margin: 0 !important;
          }
          /* Page Item & Jump (Dots) Styling */
          .custom-pagination-container .ant-pagination-item,
          .custom-pagination-container .ant-pagination-jump-prev,
          .custom-pagination-container .ant-pagination-jump-next {
            order: 2 !important;
            border-radius: 4px !important;
            height: 28px !important;
            min-width: 28px !important;
            line-height: 26px !important;
            background: white !important;
            font-family: 'Lexend', sans-serif !important;
            margin: 0 4px !important;
            font-size: 14px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-pagination-container .ant-pagination-item {
            border: 0.8px solid #4682B4 !important;
          }
          .custom-pagination-container .ant-pagination-jump-prev,
          .custom-pagination-container .ant-pagination-jump-next {
            border: none !important;
          }
          /* Hide non-active items on small screens */
          @media (max-width: 767px) {
            .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              display: none !important;
            }
          }
          .custom-pagination-container .ant-pagination-item-active {
            border-color: #4682B4 !important;
          }
          .custom-pagination-container .ant-pagination-item-active a {
            color: #1F2A37 !important;
            font-weight: 500 !important;
          }
          /* Options/Page Changer Styling */
          .custom-pagination-container .ant-pagination-options {
            order: 3 !important;
            margin-left: 8px !important;
            display: block !important; /* Ensure it is shown on mobile */
          }
          .custom-pagination-container .ant-select-selector {
            background-color: #032B440D !important;
            border: none !important;
            border-radius: 4px !important;
            height: 28px !important;
            font-family: 'Lexend', sans-serif !important;
            font-size: 12px !important;
            font-weight: 300 !important;
            color: #1F2A37 !important;
            padding: 0 10px !important;
            display: flex !important;
            align-items: center !important;
          }
          .custom-pagination-container .ant-select-selection-item {
            line-height: 28px !important;
            color: #1F2A37 !important;
          }
          .custom-pagination-container .ant-select-arrow {
            color: #1F2A37 !important;
          }
        `}} />
        <Pagination
          className="custom-pagination-container"
          current={currentPage}
          pageSize={pageSize}
          total={filteredUsers.length}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          showSizeChanger
          showTotal={(total) => `Total ${total} users`}
          itemRender={(page, type, originalElement) => {
            if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
            if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
            return originalElement;
          }}
        />
      </div>

      {/* Reset Password Modal */}
      <Modal
        title={null}
        open={resetPasswordModal.visible}
        onCancel={() => setResetPasswordModal({ visible: false, user: null })}
        footer={null}
        centered
        width={450}
        className="premium-modal"
      >
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-[#4682B4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyOutlined className="text-[28px] text-[#4682B4]" />
          </div>
          <h3 className="font-['Lexend',sans-serif] text-[20px] font-bold text-[#1e293b] mb-2">Reset Password</h3>
          <p className="text-[#64748b] text-[14px] font-medium mb-6">
            A password reset link will be sent to <span className="text-[#1e293b] font-bold">{resetPasswordModal.user?.email}</span>.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            <button
              className="h-11 bg-[#f1f5f9] text-[#475569] rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#e2e8f0] transition-all cursor-pointer border-none"
              onClick={() => setResetPasswordModal({ visible: false, user: null })}
            >
              Cancel
            </button>
            <button
              className="h-11 bg-[#4682B4] text-white rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#3a6d96] shadow-[0_4px_12px_rgba(70,130,180,0.2)] transition-all cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={confirmResetPassword}
              disabled={resetLoading}
            >
              {resetLoading ? 'Sending...' : 'Send Link'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Suspend/Activate Modal */}
      <Modal
        title={null}
        open={suspendModal.visible}
        onCancel={() => setSuspendModal({ visible: false, user: null })}
        footer={null}
        centered
        width={450}
        className="premium-modal"
      >
        <div className="p-6 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${suspendModal.user?.is_active ? 'bg-[#f59e0b]/10' : 'bg-[#10b981]/10'
            }`}>
            {suspendModal.user?.is_active ?
              <StopOutlined className="text-[28px] text-[#f59e0b]" /> :
              <PlayCircleOutlined className="text-[28px] text-[#10b981]" />
            }
          </div>
          <h3 className="font-['Lexend',sans-serif] text-[20px] font-bold text-[#1e293b] mb-2">
            {suspendModal.user?.is_active ? "Suspend User" : "Activate User"}
          </h3>
          <p className="text-[#64748b] text-[14px] font-medium mb-6">
            Are you sure you want to {suspendModal.user?.is_active ? 'suspend' : 'activate'} <span className="text-[#1e293b] font-bold">{suspendModal.user?.fullname}</span>?
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            <button
              className="h-11 bg-[#f1f5f9] text-[#475569] rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#e2e8f0] transition-all cursor-pointer border-none"
              onClick={() => setSuspendModal({ visible: false, user: null })}
            >
              Cancel
            </button>
            <button
              className={`h-11 text-white rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] transition-all cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50 ${suspendModal.user?.is_active ? 'bg-[#f59e0b] hover:bg-[#d97706] shadow-[0_4px_12px_rgba(245,158,11,0.2)]' : 'bg-[#10b981] hover:bg-[#059669] shadow-[0_4px_12px_rgba(16,185,129,0.2)]'
                }`}
              onClick={confirmToggleStatus}
              disabled={suspendLoading}
            >
              {suspendLoading ? 'Processing...' : (suspendModal.user?.is_active ? 'Suspend' : 'Activate')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        title={null}
        open={deleteModal.visible}
        onCancel={() => setDeleteModal({ visible: false, user: null })}
        footer={null}
        centered
        width={450}
        className="premium-modal"
      >
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-[#ef4444]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <DeleteOutlined className="text-[28px] text-[#ef4444]" />
          </div>
          <h3 className="font-['Lexend',sans-serif] text-[20px] font-bold text-[#1e293b] mb-2 text-danger">Delete Account</h3>
          <p className="text-[#64748b] text-[14px] font-medium mb-6">
            Permanent deletion of <span className="text-[#ef4444] font-bold">{deleteModal.user?.fullname}</span>. This action cannot be undone.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            <button
              className="h-11 bg-[#f1f5f9] text-[#475569] rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#e2e8f0] transition-all cursor-pointer border-none"
              onClick={() => setDeleteModal({ visible: false, user: null })}
            >
              Cancel
            </button>
            <button
              className="h-11 bg-[#ef4444] text-white rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#dc2626] shadow-[0_4px_12px_rgba(239,68,68,0.2)] transition-all cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={confirmDeleteUser}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={null}
        open={editModal.visible}
        onCancel={() => setEditModal({ visible: false, user: null, newRole: "", newStatus: true, newFullname: "", newEmail: "" })}
        footer={null}
        centered
        width={550}
        className="premium-modal"
      >
        <div className="p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#4682B4]/10 rounded-2xl flex items-center justify-center">
              <EditOutlined className="text-[22px] text-[#4682B4]" />
            </div>
            <div>
              <h3 className="font-['Lexend',sans-serif] text-[20px] font-bold text-[#1e293b] m-0">Edit User Profile</h3>
              <p className="text-[13px] text-[#64748b] font-medium m-0">Update account details and accessibility</p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block font-['Lexend',sans-serif] font-bold text-[13px] text-[#475569] mb-2 px-1">Full Name</label>
              <Input
                value={editModal.newFullname}
                onChange={(e) => setEditModal({ ...editModal, newFullname: e.target.value })}
                prefix={<UserOutlined className="text-[#94a3b8]" />}
                className="h-11 rounded-xl border-[#e2e8f0] font-['DM_Sans',sans-serif]"
              />
            </div>

            <div>
              <label className="block font-['Lexend',sans-serif] font-bold text-[13px] text-[#475569] mb-2 px-1">Email Address</label>
              <Input
                value={editModal.newEmail}
                onChange={(e) => setEditModal({ ...editModal, newEmail: e.target.value })}
                prefix={<MailOutlined className="text-[#94a3b8]" />}
                className="h-11 rounded-xl border-[#e2e8f0] font-['DM_Sans',sans-serif]"
              />
            </div>

            <div>
              <label className="block font-['Lexend',sans-serif] font-bold text-[13px] text-[#475569] mb-2 px-1">System Role</label>
              <Select
                value={editModal.newRole}
                onChange={(v) => setEditModal({ ...editModal, newRole: v })}
                className="w-full h-11 rounded-xl border-[#e2e8f0] [&_.ant-select-selector]:!rounded-xl"
              >
                {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                  <Option key={role} value={role}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.dotColor }} />
                      <span className="font-['Lexend',sans-serif] font-bold text-[13px]">{config.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-10">
            <button
              className="h-11 bg-[#f1f5f9] text-[#475569] rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#e2e8f0] transition-all cursor-pointer border-none"
              onClick={() => setEditModal({ visible: false, user: null, newRole: "", newStatus: true, newFullname: "", newEmail: "" })}
            >
              Discard Changes
            </button>
            <button
              className="h-11 bg-[#4682B4] text-white rounded-xl font-['Lexend',sans-serif] font-bold text-[14px] hover:bg-[#3a6d96] shadow-[0_4px_12px_rgba(70,130,180,0.2)] transition-all cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={confirmEditUser}
              disabled={editLoading}
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;